/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { Link } from "../components/ui/Link";
import {
  referralDetail,
  type ReferralDetail as ReferralDetailModel,
  type SourceFax,
} from "../api/referrals";
import { useChrome } from "../contexts/ChromeContext";
import { Badge } from "../components/ui/Badge";
import { ReferralStatusBadge } from "../components/ReferralStatusBadge";
import { SOURCE_LABELS, referralStatus } from "../lib/referrals";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Spinner } from "../components/ui/Spinner";
import { Facts, Section } from "../components/DetailBits";
import { providerName } from "../lib/registry";

/** One referral: who was referred, by whom, and where it came from. */
export function ReferralDetail() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<ReferralDetailModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  useChrome({ back: { to: "/referrals", label: "Referrals" } }, []);

  useEffect(() => {
    if (!id) {
      return;
    }
    void referralDetail(id).then((result) => {
      if (result.ok) {
        setDetail(result.value);
        setError(null);
      } else {
        setError(result.error.message);
      }
    });
  }, [id]);

  if (detail === null && error === null) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }
  if (detail === null) {
    return <ErrorMessage message={error} />;
  }

  const family = detail.clients.map((client) =>
    providerName(client.firstName, client.lastName),
  );

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          {family.length > 0 ? family.join(", ") : `Referral ${detail.id}`}
        </h1>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-[15px] text-text-secondary">
          <span>Referral {detail.id}</span>
          {detail.status && (
            <ReferralStatusBadge status={detail.status} createdAt={detail.createdAt} />
          )}
          {detail.archivedAt && <Badge tone="warning">Archived</Badge>}
        </p>
      </header>

      <div className="grid items-start gap-6 xl:grid-cols-[3fr_2fr]">
        <div className="space-y-6">
          <Section title="Referral">
            <Facts
              facts={[
                ["Created", formatDate(detail.createdAt)],
                ["Status", referralStatus(detail.status).label],
                ["Source", SOURCE_LABELS[detail.source]],
                ["Note", detail.note ?? "—"],
                ...(detail.archivedReason
                  ? [
                      ["Archived because", detail.archivedReason] as [
                        string,
                        string,
                      ],
                    ]
                  : []),
              ]}
            />
          </Section>

          <Section title="Family">
            {detail.clients.length === 0 ? (
              <p className="text-sm text-text-tertiary">
                No referred clients on this referral.
              </p>
            ) : (
              <ul className="space-y-4">
                {detail.clients.map((client) => (
                  <li key={client.id}>
                    <p className="text-sm font-medium text-text-primary">
                      {providerName(client.firstName, client.lastName)}
                      {client.type && (
                        <Badge tone="neutral" className="ml-2">
                          {client.type === "CHILD" ? "Child" : "Caregiver"}
                        </Badge>
                      )}
                    </p>
                    <Facts
                      facts={[
                        ["Email", client.email ?? "—"],
                        ["Phone", client.phoneNumber ?? "—"],
                        ["Date of birth", client.dob ?? "—"],
                        ["State", client.stateCode ?? "—"],
                      ]}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
        <Section title="Referred by">
          <Facts
            facts={[
              [
                "Organization",
                <Link
                  key="organization"
                  className="text-accent underline-offset-2 hover:underline"
                  to={`/referrals/organizations/${detail.organization.id}`}
                >
                  {detail.organization.name ??
                    `Organization ${detail.organization.id}`}
                </Link>,
              ],
              [
                "Provider",
                detail.provider ? (
                  <Link
                    key="provider"
                    className="text-accent underline-offset-2 hover:underline"
                    to={`/referrals/providers/${detail.provider.id}`}
                  >
                    {detail.provider.name ?? `Provider ${detail.provider.id}`}
                  </Link>
                ) : (
                  "—"
                ),
              ],
              ...(detail.sourceFax ? [faxFact(detail.sourceFax)] : []),
            ]}
          />
        </Section>
      </div>
    </div>
  );
}

function faxFact(fax: SourceFax): [string, ReactNode] {
  return [
    "Fax",
    <Link
      key="fax"
      className="text-accent underline-offset-2 hover:underline"
      to={`/referrals/faxes/${fax.caseId}`}
    >
      {fax.receivedAt ? `Received ${formatDate(fax.receivedAt)}` : "View fax"}
    </Link>,
  ];
}

function formatDate(value: string | null): string {
  return value
    ? new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
}
